import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowLeft, BadgeDollarSign, Building2, CreditCard, FileCheck2, FileText, Link2, Plus, QrCode, ReceiptText, Search, ShieldCheck, Split, WalletCards } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BillingModule } from '../components/BillingModule';
import InitialAvatar from '../components/InitialAvatar';
import { mapPatientFromApi, PatientRow } from '../features/patients/domain';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import { formatMoney } from '../i18n/orgFormat';
import type { SupportedLanguage } from '../i18n/translations';

type PatientListItem = {
  id: string;
  firstName: string;
  lastName: string;
  mrn: string;
  avatarUrl?: string | null;
  phone?: string | null;
  dob?: string | null;
};

type RecentInvoiceRow = {
  id: string;
  invoiceNumber?: string;
  patientId: string;
  date: string;
  total: number;
  balance: number;
  status: 'UNPAID' | 'PENDING' | 'PAID' | 'REFUNDED';
  patient?: { firstName: string; lastName: string };
  provider?: { firstName: string; lastName: string };
  items: Array<{ id: string; code: string; description: string }>;
};

type PhraseTranslator = (phrase: string) => string;

const localeByLanguage: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const billingEsOverrides: Record<string, string> = {
  'Billing and Payments': 'Facturacion y pagos',
  'Invoices, patient billing, and appointment payment in one workspace.':
    'Facturas, facturacion del paciente y pago de citas en un solo espacio.',
  'Recent Invoices': 'Facturas recientes',
  'Showing the latest 100 invoices across the organization.': 'Mostrando las ultimas 100 facturas de la organizacion.',
  'Start Invoice': 'Iniciar factura',
  'Cancel Invoice': 'Cancelar factura',
  'Search recent invoices...': 'Buscar facturas recientes...',
  Patient: 'Paciente',
  Invoice: 'Factura',
  Items: 'Items',
  Provider: 'Proveedor',
  Status: 'Estado',
  Total: 'Total',
  Balance: 'Saldo',
  Date: 'Fecha',
  Actions: 'Acciones',
  Open: 'Abrir',
  'No recent invoices match the current filter.': 'No hay facturas recientes para el filtro actual.',
  'Select a Patient': 'Selecciona un paciente',
  'Search for an existing patient to begin billing safely.': 'Busca un paciente existente para comenzar a facturar de forma segura.',
  'Search by name, MRN, or phone...': 'Buscar por nombre, MRN o telefono...',
  'No patients found': 'No se encontraron pacientes',
  'Loading patients...': 'Cargando pacientes...',
  'Loading recent invoices...': 'Cargando facturas recientes...',
  'Loading patient details...': 'Cargando detalles del paciente...',
  'Start billing': 'Comenzar facturacion',
  'Billing for selected patient below.': 'Facturando para el paciente seleccionado abajo.',
  'Select a patient to continue.': 'Selecciona un paciente para continuar.',
  'No provider assigned': 'Sin proveedor asignado',
  DOB: 'FN',
  yrs: 'anos',
  UNPAID: 'NO PAGADA',
  PENDING: 'PENDIENTE',
  PAID: 'PAGADA',
  REFUNDED: 'REEMBOLSADA',
};

const billingItOverrides: Record<string, string> = {
  'Billing and Payments': 'Fatturazione e pagamenti',
  'Invoices, patient billing, and appointment payment in one workspace.':
    'Fatture, fatturazione paziente e pagamento appuntamenti in un unico workspace.',
  'Recent Invoices': 'Fatture recenti',
  'Showing the latest 100 invoices across the organization.': 'Mostra le ultime 100 fatture dell organizzazione.',
  'Start Invoice': 'Avvia fattura',
  'Cancel Invoice': 'Annulla fattura',
  'Search recent invoices...': 'Cerca fatture recenti...',
  Patient: 'Paziente',
  Invoice: 'Fattura',
  Items: 'Voci',
  Provider: 'Medico',
  Status: 'Stato',
  Total: 'Totale',
  Balance: 'Saldo',
  Date: 'Data',
  Actions: 'Azioni',
  Open: 'Apri',
  'No recent invoices match the current filter.': 'Nessuna fattura recente corrisponde al filtro corrente.',
  'Select a Patient': 'Seleziona un paziente',
  'Search for an existing patient to begin billing safely.': 'Cerca un paziente esistente per iniziare la fatturazione in modo sicuro.',
  'Search by name, MRN, or phone...': 'Cerca per nome, MRN o telefono...',
  'No patients found': 'Nessun paziente trovato',
  'Loading patients...': 'Caricamento pazienti...',
  'Loading recent invoices...': 'Caricamento fatture recenti...',
  'Loading patient details...': 'Caricamento dettagli paziente...',
  'Start billing': 'Avvia fatturazione',
  'Billing for selected patient below.': 'Fatturazione per il paziente selezionato qui sotto.',
  'Select a patient to continue.': 'Seleziona un paziente per continuare.',
  'No provider assigned': 'Nessun medico assegnato',
  DOB: 'Nasc.',
  yrs: 'anni',
  UNPAID: 'NON PAGATA',
  PENDING: 'IN ATTESA',
  PAID: 'PAGATA',
  REFUNDED: 'RIMBORSATA',
};

const statusClasses: Record<RecentInvoiceRow['status'], string> = {
  UNPAID: 'bg-slate-100 text-slate-700 border-slate-200',
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  PAID: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REFUNDED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

function getLocale(language: SupportedLanguage) {
  return localeByLanguage[language] ?? 'en-US';
}

function createBillingTranslator(language: SupportedLanguage, tr: PhraseTranslator): PhraseTranslator {
  if (language === 'en') return tr;

  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;

    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) {
      return phrase.replace(normalized, globalTranslation);
    }

    if (language === 'es' || language === 'it') {
      const direct = language === 'it' ? billingItOverrides[normalized] : billingEsOverrides[normalized];
      if (direct) return phrase.replace(normalized, direct);
    }

    return phrase;
  };
}

function money(value: number) {
  return formatMoney(value);
}

function FinanceMetric({ icon, label, value, detail }: { icon: ReactNode; label: string; value: string; detail: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <span className="text-sky-600">{icon}</span>
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
      <p className="mt-1 text-xs text-slate-500">{detail}</p>
    </div>
  );
}

function FinanceAction({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <span className="text-sky-600">{icon}</span>
        {title}
      </div>
      <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

export default function Billing() {
  const { fetchJson } = useApiClient();
  const { tr, language } = useI18n();
  const t = createBillingTranslator(language, tr);
  const locale = getLocale(language);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [recentInvoices, setRecentInvoices] = useState<RecentInvoiceRow[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentSearch, setRecentSearch] = useState('');
  const [patientList, setPatientList] = useState<PatientListItem[]>([]);
  const [patientQuery, setPatientQuery] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [patient, setPatient] = useState<PatientRow | null>(null);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [view, setView] = useState<'table' | 'compose'>('table');
  const [financeView, setFinanceView] = useState('Open balances');
  const [chargingId, setChargingId] = useState<string | null>(null);
  const requestedPatientId = searchParams.get('patientId') || '';
  const requestedInvoiceId = searchParams.get('invoiceId');

  useEffect(() => {
    if (!requestedPatientId) return;
    setSelectedPatientId((current) => (current === requestedPatientId ? current : requestedPatientId));
    setView('compose');
    setShowPatientDialog(false);
    setShowPatientResults(false);
  }, [requestedPatientId]);

  useEffect(() => {
    let cancelled = false;
    async function loadRecentInvoices() {
      setRecentLoading(true);
      try {
        const rows = await fetchJson<RecentInvoiceRow[]>('/billing?limit=100');
        if (!cancelled) {
          setRecentInvoices(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        console.error('Failed to load recent invoices', err);
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    }
    loadRecentInvoices();
    return () => {
      cancelled = true;
    };
  }, [fetchJson]);

  useEffect(() => {
    if (!showPatientDialog || patientList.length > 0) return;
    let cancelled = false;
    async function loadPatients() {
      setLoadingPatients(true);
      try {
        const response = await fetchJson<{ data: PatientListItem[] }>('/patients?page=1&pageSize=100');
        if (!cancelled) {
          setPatientList(response.data || []);
        }
      } catch (err) {
        console.error('Failed to load patients for billing', err);
      } finally {
        if (!cancelled) setLoadingPatients(false);
      }
    }
    loadPatients();
    return () => {
      cancelled = true;
    };
  }, [fetchJson, patientList.length, showPatientDialog]);

  useEffect(() => {
    if (!selectedPatientId) return;
    let cancelled = false;
    async function loadPatient() {
      setLoadingPatient(true);
      try {
        const detail = await fetchJson<any>(`/patients/${selectedPatientId}`);
        if (!cancelled) {
          setPatient(mapPatientFromApi(detail));
          setView('compose');
          setShowPatientDialog(false);
          setShowPatientResults(false);
        }
      } catch (err) {
        console.error('Failed to load selected patient details', err);
      } finally {
        if (!cancelled) setLoadingPatient(false);
      }
    }
    loadPatient();
    return () => {
      cancelled = true;
    };
  }, [fetchJson, selectedPatientId]);

  const filteredRecentInvoices = useMemo(() => {
    const q = recentSearch.trim().toLowerCase();
    if (!q) return recentInvoices;
    return recentInvoices.filter((row) =>
      [
        row.invoiceNumber || '',
        row.patient?.firstName || '',
        row.patient?.lastName || '',
        row.provider?.firstName || '',
        row.provider?.lastName || '',
        row.items.map((item) => `${item.code} ${item.description}`).join(' '),
        row.status,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [recentInvoices, recentSearch]);

  const filteredPatients = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return patientList.slice(0, 12);
    return patientList
      .filter((p) =>
        [`${p.firstName} ${p.lastName}`, p.mrn, p.phone || '']
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 12);
  }, [patientList, patientQuery]);

  const selectedPatientMeta = useMemo(() => {
    if (!patient) return { metaLine: '', initials: 'PT' };
    const age = patient.age ? `${patient.age} ${t('yrs')}` : null;
    const metaLine = [
      patient.mrn ? `MRN ${patient.mrn}` : null,
      age,
      patient.dob ? `${t('DOB')} ${new Date(patient.dob).toLocaleDateString(locale)}` : null,
      patient.sex || null,
    ]
      .filter(Boolean)
      .join(' | ');
    const initials = `${patient.firstName?.[0] ?? ''}${patient.lastName?.[0] ?? ''}`.trim().toUpperCase() || 'PT';
    return { metaLine, initials };
  }, [locale, patient, t]);

  const financeSummary = useMemo(() => {
    const outstanding = recentInvoices.reduce((sum, row) => sum + Number(row.balance || 0), 0);
    const accountBalance = recentInvoices.reduce((sum, row) => sum + Number(row.total || 0), 0);
    const overdue = recentInvoices.filter((row) => row.balance > 0 && row.status !== 'PAID').length;
    return { outstanding, accountBalance, overdue };
  }, [recentInvoices]);

  function openPatientDialog() {
    setPatientQuery('');
    setShowPatientResults(false);
    setShowPatientDialog(true);
  }

  function returnToTable() {
    setView('table');
    setPatient(null);
    setSelectedPatientId('');
    setPatientQuery('');
    setShowPatientDialog(false);
  }

  async function chargeInvoice(row: RecentInvoiceRow) {
    setChargingId(row.id);
    try {
      // Hosted Stripe Checkout: open in a new tab; settlement is confirmed by webhook.
      const res = await fetchJson<{ url?: string }>(`/payments/invoices/${row.id}/checkout`, { method: 'POST' });
      if (res?.url) {
        window.open(res.url, '_blank', 'noopener');
      } else {
        alert(t('Could not start card payment.'));
      }
    } catch (err: any) {
      alert(err?.message || t('Could not start card payment.'));
    } finally {
      setChargingId(null);
    }
  }

  function openInvoice(row: RecentInvoiceRow) {
    const params = new URLSearchParams({ patientId: row.patientId, invoiceId: row.id });
    navigate(`/billing?${params.toString()}`);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{t('Billing and Payments')}</p>
            <h1 className="mt-1 text-2xl font-semibold text-ink">{t('Billing')}</h1>
            <p className="max-w-3xl text-sm text-slate-600">
              {t('Invoices, patient billing, and appointment payment in one workspace.')}
            </p>
          </div>
          <button
            type="button"
            onClick={view === 'compose' ? returnToTable : openPatientDialog}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
              view === 'compose'
                ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                : 'bg-lime-400 text-slate-900 hover:bg-lime-300'
            }`}
          >
            {view === 'compose' ? <ArrowLeft size={16} /> : <Plus size={16} />}
            {view === 'compose' ? t('Cancel Invoice') : t('Start Invoice')}
          </button>
        </div>
      </div>

      {view === 'table' ? (
        <section className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Finance command center</h2>
                <p className="text-sm text-slate-500">Saved views, balances, invoice actions, claims, and payment options in one billing surface.</p>
              </div>
              <select
                value={financeView}
                onChange={(event) => setFinanceView(event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
              >
                {['Open balances', 'Payments today', 'Appointments to invoice', 'Quotes pending', 'Insurance claims'].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <FinanceMetric icon={<BadgeDollarSign size={17} />} label="Outstanding balance" value={money(financeSummary.outstanding)} detail={`${financeSummary.overdue} invoices need attention`} />
              <FinanceMetric icon={<WalletCards size={17} />} label="Account balance" value={money(financeSummary.accountBalance)} detail="Includes paid, pending, and unpaid totals" />
              <FinanceMetric icon={<Building2 size={17} />} label="Stripe Capital" value="Eligible" detail="Access working-capital offers from the finance hub" />
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <FinanceAction icon={<FileText size={16} />} title="Raise invoice" detail="Create from appointment, walk-in checkout, third party, or draft quote." />
              <FinanceAction icon={<Split size={16} />} title="Split and partial pay" detail="Split across cash, card, card-on-file, manual card entry, prepaid value, or insurance." />
              <FinanceAction icon={<Link2 size={16} />} title="Payment links and QR" detail="Send checkout links, QR pay, deposits, prepaid methods, and money-on-account allocation." />
              <FinanceAction icon={<FileCheck2 size={16} />} title="Templates and credit notes" detail="Manage invoice templates, credit-note types, refunds, and client account transfers." />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Claims and insurance</h2>
            <p className="text-sm text-slate-500">Private medical billing controls for Healthcode and insurance workflows.</p>
            <div className="mt-4 space-y-3">
              <FinanceAction icon={<ShieldCheck size={16} />} title="Healthcode submission" detail="Validate insurer, policy number, diagnosis, procedure code, and submit invoice batches." />
              <FinanceAction icon={<CreditCard size={16} />} title="Insurance claims" detail="Track authorised, rejected, resubmitted, paid, and written-off claim stages." />
              <FinanceAction icon={<QrCode size={16} />} title="POS payment types" detail="Configure cash drawer, terminal, external, prepaid, voucher, wallet, and transfer methods." />
            </div>
          </div>
        </section>
      ) : null}

      {view === 'table' ? (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{t('Recent Invoices')}</h2>
              <p className="text-sm text-slate-500">{t('Showing the latest 100 invoices across the organization.')}</p>
            </div>
            <div className="relative w-full max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={recentSearch}
                onChange={(event) => setRecentSearch(event.target.value)}
                placeholder={t('Search recent invoices...')}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
              />
            </div>
          </div>

          {recentLoading ? <div className="px-4 py-6 text-sm text-slate-500">{t('Loading recent invoices...')}</div> : null}

          {!recentLoading ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-medium">{t('Patient')}</th>
                    <th className="px-4 py-3 font-medium">{t('Invoice')}</th>
                    <th className="px-4 py-3 font-medium">{t('Items')}</th>
                    <th className="px-4 py-3 font-medium">{t('Provider')}</th>
                    <th className="px-4 py-3 font-medium">{t('Status')}</th>
                    <th className="px-4 py-3 font-medium text-right">{t('Total')}</th>
                    <th className="px-4 py-3 font-medium text-right">{t('Balance')}</th>
                    <th className="px-4 py-3 font-medium">{t('Date')}</th>
                    <th className="px-4 py-3 font-medium text-right">{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRecentInvoices.map((row) => (
                    <tr key={row.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openInvoice(row)}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-slate-800">
                          {row.patient ? `${row.patient.firstName} ${row.patient.lastName}` : 'Unknown patient'}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-slate-800">{row.invoiceNumber || row.id}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        <div className="max-w-[340px] truncate">
                          {row.items.slice(0, 2).map((item) => `${item.code} ${item.description}`).join(' • ') || '-'}
                        </div>
                        {row.items.length > 2 ? <div className="text-xs text-slate-500">+{row.items.length - 2} more</div> : null}
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        {row.provider ? `${row.provider.firstName} ${row.provider.lastName}` : t('No provider assigned')}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${statusClasses[row.status]}`}>
                          {t(row.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-right font-medium text-slate-800">{money(row.total)}</td>
                      <td className="px-4 py-3 align-top text-right text-slate-600">{money(row.balance)}</td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        {new Date(row.date).toLocaleString(locale, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="inline-flex items-center justify-end gap-2">
                          {row.balance > 0 && row.status !== 'PAID' && row.status !== 'REFUNDED' ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                void chargeInvoice(row);
                              }}
                              disabled={chargingId === row.id}
                              className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-60"
                            >
                              <CreditCard size={12} />
                              {chargingId === row.id ? t('Starting…') : t('Charge card')}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openInvoice(row);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <ReceiptText size={12} />
                            {t('Open')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!filteredRecentInvoices.length ? (
                <div className="px-4 py-6 text-sm text-slate-500">{t('No recent invoices match the current filter.')}</div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {view === 'compose' ? (
        <div className="space-y-4">
          {loadingPatient ? <div className="text-sm text-slate-500">{t('Loading patient details...')}</div> : null}
          {!loadingPatient && patient ? (
            <>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                  <InitialAvatar name={`${patient.firstName} ${patient.lastName}`} src={patient.avatarUrl} alt={`${patient.firstName} ${patient.lastName}`} className="h-14 w-14 text-sm" />
                  <div className="space-y-1">
                    <p className="text-lg font-semibold text-slate-800">{patient.firstName} {patient.lastName}</p>
                    {selectedPatientMeta.metaLine ? <p className="text-sm font-medium text-slate-600">{selectedPatientMeta.metaLine}</p> : null}
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                      {patient.phone ? <span>{patient.phone}</span> : null}
                      {patient.email ? <span>{patient.email}</span> : null}
                      {patient.address ? <span>{patient.address}</span> : null}
                    </div>
                  </div>
                </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-lime-50 px-3 py-1.5 text-sm font-medium text-lime-800">
                    <ReceiptText size={14} />
                    {t('Billing for selected patient below.')}
                  </div>
                </div>
              </section>

              <BillingModule
                patientId={patient.id}
                showHeader={false}
                initialView="create"
                mode="createOnly"
                initialSelectedInvoiceId={requestedInvoiceId}
                onCancelCreate={returnToTable}
                onInvoiceCreated={() => {
                  fetchJson<RecentInvoiceRow[]>('/billing?limit=100')
                    .then((rows) => setRecentInvoices(Array.isArray(rows) ? rows : []))
                    .catch((err) => console.error('Failed to refresh recent invoices', err));
                }}
              />
            </>
          ) : null}
        </div>
      ) : null}

      {showPatientDialog ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => setShowPatientDialog(false)}>
          <div
            className="mt-10 w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{t('Select a Patient')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{t('Start billing')}</h2>
                <p className="text-sm text-slate-500">{t('Search for an existing patient to begin billing safely.')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPatientDialog(false)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="relative mt-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={patientQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setPatientQuery(value);
                  setShowPatientResults(true);
                }}
                onFocus={() => setShowPatientResults(true)}
                onBlur={() => window.setTimeout(() => setShowPatientResults(false), 120)}
                placeholder={t('Search by name, MRN, or phone...')}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
              />
              {showPatientResults ? (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[320px] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-lg">
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map((entry) => {
                      const initials = `${entry.firstName?.[0] ?? ''}${entry.lastName?.[0] ?? ''}`.trim().toUpperCase() || 'PT';
                      const rightMeta = [entry.mrn ? `MRN ${entry.mrn}` : null, entry.phone || null].filter(Boolean).join(' • ');
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          className="flex w-full items-center gap-3 border-b border-slate-100 px-3 py-3 text-left transition-colors last:border-0 hover:bg-slate-50"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setSelectedPatientId(entry.id);
                            setPatientQuery(`${entry.firstName} ${entry.lastName}`.trim());
                            setShowPatientResults(false);
                          }}
                        >
                          <InitialAvatar name={`${entry.firstName} ${entry.lastName}`} src={entry.avatarUrl} alt={`${entry.firstName} ${entry.lastName}`} className="h-10 w-10" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-slate-800">{entry.firstName} {entry.lastName}</div>
                            <div className="truncate text-xs text-slate-500">
                              {[entry.dob ? `${t('DOB')} ${new Date(entry.dob).toLocaleDateString(locale)}` : null, rightMeta].filter(Boolean).join(' • ')}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-3 text-sm text-slate-500">{t('No patients found')}</div>
                  )}
                </div>
              ) : null}
            </div>

            {loadingPatients ? <div className="mt-3 text-sm text-slate-500">{t('Loading patients...')}</div> : null}
            {!loadingPatients && !selectedPatientId ? <div className="mt-3 text-sm text-slate-500">{t('Select a patient to continue.')}</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
